/**
 * What the app itself needs to know about a plan. Prices, plan lists, Stripe ids and what a plan includes are Flashsuite's price
 * catalog's, read by workers/src/billing/catalog (docs/CATALOG.md in the Flashsuite repo); none of them is kept here.
 */

/** The plan-billing lifecycle state stored on `workspaces.plan_status`. */
export type PlanStatus =
  | "inactive" // no active subscription (the default for a new workspace: must subscribe)
  | "active"
  | "past_due"
  | "canceled";

/** A usage top-up the app adds on its own when a month's hours run out. */
export type TopupKind = "live" | "video";

/**
 * True when a workspace has a live subscription (access is granted). `active` and
 * `past_due` both count (past_due = paying but a payment is retrying: a portal
 * concern, not "no plan"); `inactive` / `canceled` do NOT: those must subscribe.
 */
export function hasActiveSubscription(status: string | null | undefined): boolean {
  return status === "active" || status === "past_due";
}

const EXPONENT: Record<string, number> = { jpy: 0, kwd: 3, bhd: 3, omr: 3, jod: 3, tnd: 3 };

/**
 * An amount in a currency's minor unit, as a person reads it: `$49`, `$49.50`, `AED 1,850`. `exact` always writes the minor
 * digits, for an invoice line (`$49.00`).
 */
export function money(minor: number, currency: string, exact = false): string {
  const code = currency.toLowerCase();
  const digits = EXPONENT[code] ?? 2;
  const value = minor / 10 ** digits;
  const whole = Number.isInteger(value);
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: code.toUpperCase(),
    minimumFractionDigits: exact || !whole ? digits : 0,
    maximumFractionDigits: digits,
  })
    .format(value)
    .replace(/ /g, " ");
}
